import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDignity, useMessages, useRoom } from 'dignity.js/react';
import {
  colorForPlayer,
  autoForfeitAbsentAtStart,
  canClaimStartForfeit,
  expireWaitingGame,
  forfeitDeadlineAt,
  isScheduledStartReached,
  isUntimedControl,
  liveClocks,
  MATCH_FORFEIT_GRACE_MS,
  preferGameState
} from '../../chess/game.js';
import { loadGame, saveGame } from '../../chess/gameStore.js';
import {
  applySignedGameAction,
  createSignedGameAction,
  createSignedGameSnapshot,
  verifySignedGameSnapshot
} from '../../chess/protocol.js';
import {
  createResultCertificateDraft,
  finalizeResultCertificate,
  gameContractHash,
  signResultCertificate,
  verifyCertificateSignature,
  verifyResultCertificate
} from '../../p2p/certificates.js';
import {
  certificateChainHead,
  cloneCertificateForStore,
  saveCertificate
} from '../../p2p/certificateStore.js';
import {
  createLiveTimeWeave,
  computeTimeWeaveProof,
  loadOrCalibrateVdf,
  verifyTimeWeaveProof
} from '../../p2p/timeWeave.js';
import {
  hasBothVdfProofs,
  opponentPlayerId,
  resolveGameConnectPeers,
  resolveGameFanoutPeers
} from '../../p2p/gamePeers.js';
import {
  checkpointMatchesGame,
  createResumeCheckpoint,
  decodeResumeCheckpoint,
  encodeResumeCheckpoint,
  isDualSignedCheckpoint,
  signResumeCheckpoint,
  verifyResumeSignature
} from '../../chess/resume.js';
import {
  allowsMatchExport,
  buildMatchExport,
  downloadMatchExport
} from '../../chess/matchExport.js';
import { createNodeConfig, LOBBY_SCOPE } from '../../p2p/node.js';
import useBootstrapPeerConnection from '../../p2p/useBootstrapPeerConnection.js';
import useStableRoomOptions from '../../p2p/useStableRoomOptions.js';
import { fetchGameRendezvousPeers } from '../../p2p/gameRendezvous.js';
import { publishLocalGameUpdate, publishLocalGameWire, subscribeLocalGameUpdate, subscribeLocalGameWire } from '../../p2p/gameLocalBus.js';
import { lobbyDebug } from '../../p2p/lobbyDebug.js';
import { formatDisplayName } from '../../p2p/displayName.js';
import { getPlayerProfile } from '../../p2p/playerProfiles.js';
import { publicUrl } from '../../app/publicUrl.js';
import {
  hasRecoveryBackupAcknowledged,
  markRecoveryBackupAcknowledged
} from '../../p2p/recoveryBackup.js';
import {
  isPeerIdTakenError,
  rotateIdentityPeerSession
} from '../../p2p/identity.js';
import { parseGameRole, parsePeerId } from '../../security/input.js';
import { checkCueForUpdate, moveGivesCheck } from '../../chess/checkCue.js';
import { useI18n } from '../../i18n/useI18n.js';
import ChessBoard from '../../ui/ChessBoard.jsx';
import { playBoardMoveSound, playCheckSound, unlockMoveSounds } from '../../ui/moveSounds.js';
import RecoveryPhraseModal from '../settings/RecoveryPhraseModal.jsx';
import ReleaseNote from '../../ui/ReleaseNote.jsx';
import { VARIANTS } from '../../chess/variants.js';
import {
  deliverChallengeMessage,
  formatChallengeDeadline,
  formatCountdown
} from '../../p2p/challenges.js';
import {
  clearPendingOutgoingChallenge,
  loadPendingOutgoingChallenge
} from '../../p2p/pendingOutgoingChallenge.js';
import {
  publishLobbyChallengeAdvert,
  queueLobbyChallengeAdvert
} from '../../p2p/lobbyRendezvous.js';
import { NICKNAME_KEY } from '../../p2p/playerProfiles.js';

function formatClock(milliseconds) {
  if (milliseconds == null || !Number.isFinite(milliseconds)) {
    return '∞';
  }
  const safe = Math.max(0, milliseconds);
  const minutes = Math.floor(safe / 60000);
  const seconds = Math.floor((safe % 60000) / 1000);
  const tenths = Math.floor((safe % 1000) / 100);
  return safe < 10000
    ? `${minutes}:${String(seconds).padStart(2, '0')}.${tenths}`
    : `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function playerFromIdentity(identity, chainHead = null) {
  return {
    playerId: identity.playerId,
    nickname: localStorage.getItem(NICKNAME_KEY) || `Player ${identity.playerId.slice(-4)}`,
    publicKey: identity.publicKey,
    chainHead
  };
}

const CHECK_POPUP_MS = 1800;

export default function GameRoute({ identity, gameId, onIdentityChange }) {
  const { t } = useI18n();
  const search = useMemo(() => new URLSearchParams(window.location.search), []);
  const urlBootstrapPeer = parsePeerId(search.get('peer')) || '';
  const [discoveredPeers, setDiscoveredPeers] = useState([]);
  const bootstrapPeer = useMemo(() => {
    const live = discoveredPeers.find((peerId) => peerId && peerId !== identity.peerId);
    return live || urlBootstrapPeer;
  }, [discoveredPeers, identity.peerId, urlBootstrapPeer]);
  const requestedRole = parseGameRole(search.get('role'));
  const scope = `game:${gameId}`;
  const [game, setGame] = useState(null);
  const gameRef = useRef(null);
  const [notice, setNotice] = useState('Loading local game state…');
  const [checkAlert, setCheckAlert] = useState(false);
  const seenMovePly = useRef(undefined);
  const checkTimer = useRef(null);
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState(false);
  const [resultCertificate, setResultCertificate] = useState(null);
  const [resumeCheckpoint, setResumeCheckpoint] = useState(() => {
    const encoded = search.get('resume');
    if (!encoded) {
      return null;
    }
    try {
      return decodeResumeCheckpoint(encoded);
    } catch {
      return null;
    }
  });
  const joinSent = useRef(false);
  const certificateSent = useRef(false);
  const echoedCertificateHashes = useRef(new Set());
  const timeWeave = useRef(null);
  const timeWeaveStarting = useRef(false);
  const timeWeaveFinalizing = useRef(false);
  const vdfProofs = useRef({});
  const [vdfProofVersion, setVdfProofVersion] = useState(0);
  const processedMessages = useRef(0);
  const wireInboxRef = useRef([]);
  const [wireTick, setWireTick] = useState(0);
  const [recoveryGateOpen, setRecoveryGateOpen] = useState(
    () => !hasRecoveryBackupAcknowledged(identity.playerId)
  );

  useEffect(() => () => window.clearTimeout(checkTimer.current), []);

  function syncCheckCue(nextGame) {
    const cue = checkCueForUpdate(seenMovePly.current, nextGame);
    seenMovePly.current = cue.ply;
    if (cue.announce) {
      setCheckAlert(true);
      playCheckSound();
      window.clearTimeout(checkTimer.current);
      checkTimer.current = window.setTimeout(() => setCheckAlert(false), CHECK_POPUP_MS);
      return;
    }
    if (cue.clear) {
      setCheckAlert(false);
      window.clearTimeout(checkTimer.current);
    }
  }

  useEffect(() => {
    function unlockOnInteraction() {
      unlockMoveSounds();
    }
    window.addEventListener('pointerdown', unlockOnInteraction, { once: true });
    window.addEventListener('keydown', unlockOnInteraction, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlockOnInteraction);
      window.removeEventListener('keydown', unlockOnInteraction);
    };
  }, []);

  const nodeConfig = useMemo(() => createNodeConfig(identity), [identity]);
  const { node, status, error: nodeError } = useDignity(nodeConfig);
  const runningNode = status === 'running' ? node : null;
  const peerRotateInflightRef = useRef(false);

  useEffect(() => {
    if (!nodeError || !isPeerIdTakenError(nodeError) || peerRotateInflightRef.current) {
      return undefined;
    }
    peerRotateInflightRef.current = true;
    let cancelled = false;
    void rotateIdentityPeerSession(identity)
      .then((next) => {
        if (!cancelled) {
          onIdentityChange?.(next);
          setNotice('Peer session refreshed — reconnecting…');
        }
      })
      .catch(() => undefined)
      .finally(() => {
        peerRotateInflightRef.current = false;
      });
    return () => {
      cancelled = true;
    };
  }, [identity, nodeError, onIdentityChange]);

  useBootstrapPeerConnection(runningNode, bootstrapPeer);

  // Host board tab has a different PeerJS id than the lobby challenge link.
  // Rediscover live game peers via PeerBasket so joiners find the host tab.
  useEffect(() => {
    if (!runningNode || !identity?.peerId || !gameId) {
      return undefined;
    }
    let cancelled = false;
    const refresh = async () => {
      const snapshot = await fetchGameRendezvousPeers(identity.peerId, gameId, {
        force: true
      });
      if (cancelled) {
        return;
      }
      const peers = (snapshot?.peers || []).filter(Boolean);
      if (peers.length) {
        setDiscoveredPeers(peers);
        await runningNode.ensureConnectedToPeers(peers).catch(() => undefined);
        lobbyDebug('game-rendezvous-dial', {
          gameId,
          role: requestedRole,
          peers,
          selfPeerId: identity.peerId
        });
      }
    };
    void refresh();
    const timer = window.setInterval(() => {
      void refresh();
    }, 3_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [gameId, identity.peerId, requestedRole, runningNode]);

  // Same-tab host boards own the challenge peerId — keep retrying delivery
  // until the opponent seats (lobby tab no longer stays open to do this).
  useEffect(() => {
    if (!runningNode || requestedRole !== 'host') {
      return undefined;
    }
    const pending = loadPendingOutgoingChallenge();
    if (!pending?.challenge || pending.challenge.gameId !== gameId) {
      return undefined;
    }
    if (game?.players?.black) {
      clearPendingOutgoingChallenge();
      return undefined;
    }
    if (game && game.status !== 'waiting') {
      return undefined;
    }

    const push = async () => {
      const currentPending = loadPendingOutgoingChallenge();
      if (!currentPending?.challenge || currentPending.challenge.gameId !== gameId) {
        return;
      }
      if (gameRef.current?.players?.black) {
        clearPendingOutgoingChallenge();
        return;
      }
      queueLobbyChallengeAdvert(currentPending.challenge);
      const candidatePeerIds = [currentPending.targetPeerId].filter(Boolean);
      try {
        await deliverChallengeMessage(runningNode, currentPending.challenge, {
          targetPeerId: candidatePeerIds[0] || '',
          candidatePeerIds,
          broadcastScope: LOBBY_SCOPE,
          preferPeerIds: candidatePeerIds,
          publishOutOfBand: (payload) => publishLobbyChallengeAdvert(payload, { force: true }),
          attempts: 2,
          settleMs: 400
        });
        lobbyDebug('host-board-challenge-retry', {
          gameId,
          targetPlayerId: currentPending.targetPlayerId
        });
      } catch (error) {
        lobbyDebug('host-board-challenge-retry-fail', {
          gameId,
          message: error?.message || String(error)
        });
      }
    };

    void push();
    const timer = window.setInterval(() => {
      void push();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [game?.players?.black, game?.status, gameId, requestedRole, runningNode]);

  const roomOptions = useStableRoomOptions({
    bootstrapPeer,
    bootstrapPeerIds: discoveredPeers,
    metadata: {
      playerId: identity.playerId,
      nickname: playerFromIdentity(identity).nickname,
      role: requestedRole,
      gameId
    },
    heartbeatIntervalMs: 4000,
    ttlMs: 16000
  });
  const { joined, error: roomError, peers, connectionStats } = useRoom(
    runningNode,
    scope,
    roomOptions
  );
  const gameMessageFilter = useCallback(
    (message) =>
      message.type?.startsWith('dechess-game-') && message.payload?.gameId === gameId,
    [gameId]
  );
  const messages = useMessages(runningNode, gameMessageFilter);
  const gameConnectPeers = useMemo(
    () => {
      // Prefer live board peers from the game basket, but always keep the challenge
      // URL peer (lobby tab) — it can proxy signed state while the board tab dials.
      const discovered = discoveredPeers
        .filter((peerId) => peerId && peerId !== identity.peerId)
        .filter((peerId, index, list) => list.indexOf(peerId) === index);
      const fromRoom = resolveGameConnectPeers(peers, game, identity, bootstrapPeer);
      const urlPeer = urlBootstrapPeer && urlBootstrapPeer !== identity.peerId
        ? urlBootstrapPeer
        : '';
      return [...discovered, ...fromRoom, urlPeer]
        .filter((peerId) => peerId && peerId !== identity.peerId)
        .filter((peerId, index, list) => list.indexOf(peerId) === index);
    },
    [bootstrapPeer, discoveredPeers, game, identity, peers, urlBootstrapPeer]
  );
  // Moves / live snapshots must reach spectators too — not only seated seats.
  const gameFanoutPeers = useMemo(
    () => resolveGameFanoutPeers({
      peers,
      game,
      identity,
      bootstrapPeer: bootstrapPeer || urlBootstrapPeer,
      openPeerIds: connectionStats?.peerIds || [],
      discoveredPeers
    }),
    [
      bootstrapPeer,
      connectionStats?.peerIds,
      discoveredPeers,
      game,
      identity,
      peers,
      urlBootstrapPeer
    ]
  );
  const seatedOpponentPlayerId = game
    ? opponentPlayerId(game, identity.playerId)
    : null;

  const hydratedRef = useRef(false);
  const [hydrated, setHydrated] = useState(false);
  const pendingRemoteRef = useRef([]);

  const adoptGame = useCallback((nextGame, { replace = false } = {}) => {
    if (!nextGame) {
      return;
    }
    const merged = replace ? nextGame : preferGameState(gameRef.current, nextGame);
    if (!merged || merged === gameRef.current) {
      return;
    }
    // Same object identity avoided; also skip no-op ready merges already handled.
    if (
      !replace
      && gameRef.current
      && merged.revision === gameRef.current.revision
      && merged.status === gameRef.current.status
      && merged.startReady?.white === gameRef.current.startReady?.white
      && merged.startReady?.black === gameRef.current.startReady?.black
      && merged.startConfirmedAt?.white === gameRef.current.startConfirmedAt?.white
      && merged.startConfirmedAt?.black === gameRef.current.startConfirmedAt?.black
      && merged.startedAt === gameRef.current.startedAt
      && merged.fen === gameRef.current.fen
      && (merged.moves?.length || 0) === (gameRef.current.moves?.length || 0)
    ) {
      return;
    }
    gameRef.current = merged;
    setGame(merged);
    syncCheckCue(merged);
    saveGame(merged).catch((error) => setNotice(`Local save failed: ${error.message}`));
  }, []);

  const broadcast = useCallback(
    async (type, payload, connectToPeers = []) => {
      if (!runningNode) {
        throw new Error('P2P node is not ready');
      }
      await runningNode.broadcastMessage(
        type,
        { gameId, ...payload },
        {
          broadcastScope: scope,
          connectToPeers
        }
      );
    },
    [gameId, runningNode, scope]
  );

  /**
   * Deliver game protocol to specific peers.
   * Uses broadcast encryption + fanout (shared app password) — sendDirectMessage
   * requires a trusted encryption key we often lack before presence arrives from
   * PeerBasket-discovered board peers.
   */
  const sendGameDirect = useCallback(
    async (type, payload, peerIds = []) => {
      if (!runningNode) {
        throw new Error('P2P node is not ready');
      }
      const targets = [...new Set(
        (peerIds || []).filter((peerId) => peerId && peerId !== identity.peerId)
      )];

      for (const peer of peers || []) {
        if (targets.includes(peer?.peerId) && peer?.metadata?.publicKey) {
          runningNode.trustPeerFromMetadata?.(peer.peerId, peer.metadata);
        }
      }

      if (!targets.length) {
        await broadcast(type, payload, []);
        return;
      }

      try {
        await runningNode.broadcastMessage(
          type,
          { gameId, ...payload },
          {
            broadcastScope: scope,
            connectToPeers: targets,
            fanoutPeerIds: targets
          }
        );
        lobbyDebug('game-fanout-send', {
          type,
          gameId,
          targets
        });
      } catch (error) {
        lobbyDebug('game-fanout-send-fail', {
          type,
          gameId,
          targets,
          message: error?.message || String(error)
        });
        throw error;
      }
    },
    [broadcast, gameId, identity.peerId, peers, runningNode, scope]
  );

  const signedSnapshotForWire = useCallback(
    (currentGame) => {
      // Sign the JSON-wire shape so PeerJS json serialization cannot break verify.
      const wireGame = JSON.parse(JSON.stringify(currentGame));
      return createSignedGameSnapshot(wireGame, identity);
    },
    [identity]
  );

  useEffect(() => {
    let cancelled = false;
    hydratedRef.current = false;
    setHydrated(false);
    pendingRemoteRef.current = [];
    loadGame(gameId)
      .then((stored) => {
        if (cancelled) {
          return;
        }
        if (stored) {
          adoptGame(stored, { replace: true });
          setNotice(
            stored.moves?.length
              ? `Recovered game from this browser (rev ${stored.revision}, ${stored.moves.length} moves).`
              : 'Recovered game state from this browser.'
          );
          lobbyDebug('game-hydrate', {
            gameId,
            revision: stored.revision,
            status: stored.status,
            moves: stored.moves?.length || 0
          });
        } else {
          setNotice('Waiting for the game host…');
        }
        hydratedRef.current = true;
        setHydrated(true);
        const pending = pendingRemoteRef.current.splice(0);
        for (const remote of pending) {
          adoptGame(remote);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          hydratedRef.current = true;
          setHydrated(true);
          setNotice(`Could not load game: ${error.message}`);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adoptGame, gameId]);

  // Flush the latest board into IDB/localStorage before the tab goes away.
  useEffect(() => {
    const persist = () => {
      const current = gameRef.current;
      if (!current?.id) {
        return;
      }
      void saveGame(current).catch(() => undefined);
    };
    const onHide = () => {
      if (document.visibilityState === 'hidden') {
        persist();
      }
    };
    window.addEventListener('pagehide', persist);
    window.addEventListener('beforeunload', persist);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', persist);
      window.removeEventListener('beforeunload', persist);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, []);

  // Lobby tab proxies the challenge peerId; both seats must adopt that relay
  // or a move that lands on the lobby tab never reaches the board.
  useEffect(() => {
    return subscribeLocalGameUpdate((message) => {
      if (message.gameId !== gameId || !message.game) {
        return;
      }
      // Do not let a lobby push race ahead of IndexedDB hydrate and wipe moves.
      if (!hydratedRef.current) {
        pendingRemoteRef.current.push(message.game);
        return;
      }
      const before = gameRef.current;
      adoptGame(message.game);
      if (gameRef.current !== before) {
        if (gameRef.current?.status === 'waiting' || gameRef.current?.status === 'starting') {
          setNotice(
            gameRef.current?.players?.black
              ? 'Opponent joined via lobby relay.'
              : 'Game state synced from lobby tab.'
          );
        }
        lobbyDebug('game-local-bus-adopt', {
          gameId,
          revision: gameRef.current?.revision,
          source: message.source,
          moves: gameRef.current?.moves?.length || 0
        });
      }
    });
  }, [adoptGame, gameId]);

  // Claim the black seat after hydrate or a host snapshot (works across hard refresh).
  useEffect(() => {
    if (
      !hydrated
      || !runningNode
      || !game
      || game.status !== 'waiting'
      || game.players?.black
      || requestedRole !== 'join'
      || game.players?.white?.playerId === identity.playerId
      || joinSent.current
    ) {
      return undefined;
    }
    joinSent.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const seated = gameRef.current;
        if (!seated || seated.players?.black) {
          joinSent.current = false;
          return;
        }
        const chainHead = await certificateChainHead(identity.playerId);
        if (cancelled) {
          return;
        }
        const joinAction = createSignedGameAction({
          game: seated,
          type: 'join',
          payload: { player: playerFromIdentity(identity, chainHead) },
          identity
        });
        const joinedGame = applySignedGameAction(seated, joinAction);
        adoptGame(joinedGame, { replace: true });
        publishLocalGameUpdate(joinedGame, { source: 'join-board-action' });
        const targets = bootstrapPeer
          ? [bootstrapPeer]
          : (gameConnectPeers.length ? gameConnectPeers : []);
        await sendGameDirect('dechess-game-action', { action: joinAction }, targets);
        if (!cancelled) {
          setNotice(
            joinedGame.status === 'playing'
              ? 'Both players are ready. The clock has started.'
              : 'Seat claimed. Begin when you are online and ready to play.'
          );
        }
      } catch (error) {
        joinSent.current = false;
        if (!cancelled) {
          setNotice(`Join failed: ${error.message}`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    adoptGame,
    bootstrapPeer,
    game,
    gameConnectPeers,
    hydrated,
    identity,
    requestedRole,
    runningNode,
    sendGameDirect
  ]);

  useEffect(() => {
    return subscribeLocalGameWire((envelope) => {
      if (envelope.gameId !== gameId || envelope.source === 'board') {
        return;
      }
      wireInboxRef.current.push({
        type: envelope.messageType,
        senderId: envelope.senderId || 'lobby-relay',
        payload: { gameId: envelope.gameId, ...(envelope.payload || {}) }
      });
      setWireTick((value) => value + 1);
    });
  }, [gameId]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const current = gameRef.current;
    if (!current || current.status !== 'waiting' || !current.joinDeadlineAt) {
      return undefined;
    }
    if (Date.now() <= current.joinDeadlineAt) {
      return undefined;
    }
    const expired = expireWaitingGame(current, Date.now());
    if (expired !== current) {
      adoptGame(expired);
      setNotice('Join window expired before your opponent arrived.');
    }
    return undefined;
  }, [adoptGame, game?.joinDeadlineAt, game?.status, now]);

  useEffect(() => {
    const current = gameRef.current;
    if (!current || current.status !== 'starting') {
      return undefined;
    }
    const next = autoForfeitAbsentAtStart(current, Date.now());
    if (next !== current) {
      adoptGame(next);
      if (next.result?.endReason === 'forfeit') {
        setNotice('Opponent forfeited by failing to appear at the scheduled start.');
      } else if (next.result?.endReason === 'no-show') {
        setNotice('Neither player confirmed in time. Match cancelled.');
      } else if (next.status === 'playing') {
        setNotice('Scheduled start reached. The clock is running.');
      }
    }
    return undefined;
  }, [adoptGame, game?.scheduledStartAt, game?.startReady, game?.status, now]);

  // Do not wait for discovery-room `joined` — PeerBasket links can be open first.
  // Hosts also pull while confirming start — otherwise a lobby-relayed opponent
  // ready-start can leave the board tab stuck on "waiting for opponent".
  useEffect(() => {
    if (!runningNode) {
      return undefined;
    }
    if (!gameConnectPeers.length && !(connectionStats?.peerIds || []).length) {
      return undefined;
    }

    const requestState = async () => {
      const current = gameRef.current;
      // A spectator or joiner has no local game yet. Keep asking connected peers.
      const needsInitialSnapshot = !current
        && (requestedRole === 'spectator' || requestedRole === 'join');
      // Playing games must keep asking. A single dropped move leaves the two
      // browsers on different revisions and opposite turns, so neither can move.
      const playing = current?.status === 'playing';
      if (!needsInitialSnapshot && !playing) {
        if (!current) {
          return;
        }
        if (current.status === 'finished') {
          return;
        }
        if (
          requestedRole === 'host'
          && current.status === 'starting'
          && current.startReady?.white
          && current.startReady?.black
        ) {
          return;
        }
        if (requestedRole === 'host' && current.status === 'waiting') {
          // Host already has local waiting state; joiners request.
          return;
        }
      }
      const targets = gameConnectPeers.length
        ? gameConnectPeers
        : (connectionStats?.peerIds || []);
      if (!targets.length) {
        return;
      }
      try {
        await sendGameDirect(
          'dechess-game-state-request',
          { requester: playerFromIdentity(identity) },
          targets
        );
        lobbyDebug('game-state-request', { gameId, targets, role: requestedRole });
      } catch (error) {
        if (gameRef.current?.status === 'playing') {
          lobbyDebug('game-state-request-fail', {
            gameId,
            message: error?.message || String(error)
          });
          return;
        }
        setNotice(`State request failed: ${error.message}`);
      }
    };

    void requestState();
    const timer = setInterval(() => {
      void requestState();
    }, 600);
    return () => clearInterval(timer);
  }, [
    connectionStats?.peerIds,
    gameConnectPeers,
    game?.status,
    game?.startReady?.white,
    game?.startReady?.black,
    gameId,
    identity,
    requestedRole,
    runningNode,
    sendGameDirect
  ]);

  // Shared IndexedDB with the lobby tab: pull a ready-start or a newer
  // in-game revision that landed on the lobby peer.
  useEffect(() => {
    if (game?.status !== 'starting' && game?.status !== 'playing') {
      return undefined;
    }
    let cancelled = false;
    const pull = async () => {
      try {
        const stored = await loadGame(gameId);
        if (!cancelled && stored) {
          adoptGame(stored);
        }
      } catch {
        // Ignore transient IDB failures.
      }
    };
    void pull();
    const timer = window.setInterval(() => {
      void pull();
    }, 400);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [adoptGame, game?.status, gameId]);

  // While waiting for the other confirmation, re-assert our ready-start and push state.
  useEffect(() => {
    if (!runningNode || game?.status !== 'starting') {
      return undefined;
    }
    const myColorLocal = colorForPlayer(game, identity.playerId);
    if (!myColorLocal || !game.startReady?.[myColorLocal]) {
      return undefined;
    }
    const opponentColorLocal = myColorLocal === 'white' ? 'black' : 'white';
    if (game.startReady?.[opponentColorLocal]) {
      return undefined;
    }

    const resync = async () => {
      const current = gameRef.current;
      if (!current || current.status !== 'starting') {
        return;
      }
      if (!current.startReady?.[myColorLocal] || current.startReady?.[opponentColorLocal]) {
        return;
      }
      const targets = gameConnectPeers.length
        ? gameConnectPeers
        : (connectionStats?.peerIds || []);
      publishLocalGameUpdate(current, { source: 'ready-start-resync' });
      const action = createSignedGameAction({
        game: current,
        type: 'ready-start',
        identity
      });
      const snapshot = signedSnapshotForWire(current);
      publishLocalGameWire({
        gameId,
        messageType: 'dechess-game-action',
        payload: { action },
        senderId: identity.peerId,
        source: 'board'
      });
      publishLocalGameWire({
        gameId,
        messageType: 'dechess-game-state',
        payload: { snapshot },
        senderId: identity.peerId,
        source: 'board'
      });
      if (!targets.length) {
        return;
      }
      try {
        await sendGameDirect(
          'dechess-game-state',
          { snapshot },
          targets
        );
        await sendGameDirect(
          'dechess-game-action',
          { action },
          targets
        );
        lobbyDebug('ready-start-resync', { gameId, targets, color: myColorLocal });
      } catch (error) {
        lobbyDebug('ready-start-resync-fail', {
          gameId,
          message: error?.message || String(error)
        });
      }
    };

    void resync();
    const timer = window.setInterval(() => {
      void resync();
    }, 700);
    return () => window.clearInterval(timer);
  }, [
    connectionStats?.peerIds,
    game,
    gameConnectPeers,
    gameId,
    identity,
    runningNode,
    sendGameDirect,
    signedSnapshotForWire
  ]);

  // Backup state push while playing. Keep this sparse — a 700ms PoW+KDF
  // snapshot loop was saturating the wire and delaying live moves.
  useEffect(() => {
    if (!runningNode) {
      return undefined;
    }
    const pushState = async () => {
      const current = gameRef.current;
      if (!current || !['waiting', 'starting', 'playing', 'finished', 'aborted'].includes(current.status)) {
        return;
      }
      if (current.status === 'playing' || current.status === 'finished' || current.status === 'aborted') {
        publishLocalGameUpdate(current, { source: 'board-playing-resync' });
      }
      const targets = gameFanoutPeers.length
        ? gameFanoutPeers
        : (connectionStats?.peerIds || []);
      if (!targets.length) {
        return;
      }
      try {
        await sendGameDirect(
          'dechess-game-state',
          { snapshot: signedSnapshotForWire(current) },
          targets
        );
        lobbyDebug('game-push-state', {
          gameId,
          targets,
          revision: current.revision,
          status: current.status,
          role: requestedRole
        });
      } catch (error) {
        lobbyDebug('game-push-fail', {
          gameId,
          message: error?.message || String(error)
        });
      }
    };
    void pushState();
    const timer = window.setInterval(() => {
      void pushState();
    }, currentStatusNeedsFastPush() ? 900 : 2_200);
    return () => window.clearInterval(timer);

    function currentStatusNeedsFastPush() {
      const status = gameRef.current?.status;
      return status === 'waiting' || status === 'starting';
    }
  }, [
    connectionStats?.peerIds,
    game?.revision,
    game?.status,
    gameFanoutPeers,
    gameId,
    requestedRole,
    runningNode,
    sendGameDirect,
    signedSnapshotForWire
  ]);

  // After a bad connection or tab wake, exchange snapshots immediately so a
  // missed opponent move is recovered without waiting on the 1.2–1.5s timers.
  const previousOpenCountRef = useRef(0);
  useEffect(() => {
    if (!runningNode) {
      return undefined;
    }

    const resyncNow = async (reason) => {
      const current = gameRef.current;
      if (!current || !['waiting', 'starting', 'playing'].includes(current.status)) {
        return;
      }
      const targets = gameFanoutPeers.length
        ? gameFanoutPeers
        : (connectionStats?.peerIds || []);
      if (!targets.length) {
        return;
      }
      lobbyDebug('game-force-resync', {
        gameId,
        reason,
        targets,
        revision: current.revision,
        moves: current.moves?.length || 0
      });
      try {
        await sendGameDirect(
          'dechess-game-state-request',
          { requester: playerFromIdentity(identity) },
          targets
        );
      } catch {
        // Peer may still be negotiating ICE.
      }
      try {
        await sendGameDirect(
          'dechess-game-state',
          { snapshot: signedSnapshotForWire(current) },
          targets
        );
        if (current.status === 'playing' || current.status === 'starting') {
          publishLocalGameUpdate(current, { source: 'board-force-resync' });
        }
      } catch {
        // Retry via the periodic push interval.
      }
    };

    const openCount = connectionStats?.openCount || 0;
    const previousOpen = previousOpenCountRef.current;
    previousOpenCountRef.current = openCount;
    if (previousOpen === 0 && openCount > 0) {
      void resyncNow('peer-link-restored');
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void resyncNow('tab-visible');
      }
    };
    const onOnline = () => {
      void resyncNow('browser-online');
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [
    connectionStats?.openCount,
    connectionStats?.peerIds,
    gameFanoutPeers,
    gameId,
    identity,
    runningNode,
    sendGameDirect,
    signedSnapshotForWire
  ]);

  // Only rewind when the message list is replaced/shrunk (node remount), not when
  // useDignity returns a fresh node object identity each render.
  useEffect(() => {
    if (messages.length < processedMessages.current) {
      processedMessages.current = 0;
    }
  }, [messages.length]);

  useEffect(() => {
    if (!runningNode && wireInboxRef.current.length === 0) {
      return;
    }
    if (
      processedMessages.current >= messages.length
      && wireInboxRef.current.length === 0
    ) {
      return;
    }

    const pending = [
      ...messages.slice(processedMessages.current),
      ...wireInboxRef.current.splice(0)
    ];
    processedMessages.current = messages.length;

    const process = async () => {
      for (const message of pending) {
        try {
          const current = gameRef.current;

          if (message.type === 'dechess-game-state-request') {
            if (current) {
              await sendGameDirect(
                'dechess-game-state',
                { snapshot: signedSnapshotForWire(current) },
                [message.senderId]
              );
            }
            continue;
          }

          if (message.type === 'dechess-game-state') {
            const signedSnapshot = message.payload.snapshot;
            if (!verifySignedGameSnapshot(signedSnapshot)) {
              lobbyDebug('game-snapshot-verify-fail', {
                gameId,
                senderId: message.senderId,
                signerId: signedSnapshot?.signerId
              });
              throw new Error('Invalid game snapshot signature');
            }
            const snapshot = signedSnapshot.game;
            if (snapshot.id !== gameId) {
              continue;
            }
            if (!hydratedRef.current) {
              pendingRemoteRef.current.push(snapshot);
              continue;
            }
            adoptGame(snapshot);
            continue;
          }

          if (message.type === 'dechess-game-resume-proposal' && current) {
            const checkpoint = message.payload.checkpoint;
            const signer = [current.players.white, current.players.black]
              .find((player) => player?.playerId === message.payload.signerId);
            if (
              !checkpointMatchesGame(checkpoint, current)
              || !verifyResumeSignature(checkpoint, signer)
            ) {
              throw new Error('Invalid resume checkpoint proposal');
            }
            const finalized = signResumeCheckpoint(checkpoint, identity);
            if (!isDualSignedCheckpoint(finalized, current)) {
              throw new Error('Resume checkpoint requires both players');
            }
            setResumeCheckpoint(finalized);
            const finalPayload = { checkpoint: finalized };
            publishLocalGameWire({
              gameId: current.id,
              messageType: 'dechess-game-resume-final',
              payload: finalPayload,
              senderId: identity.peerId,
              source: 'board'
            });
            await sendGameDirect(
              'dechess-game-resume-final',
              finalPayload,
              [message.senderId].filter(Boolean)
            );
            setNotice('Dual-signed resume checkpoint verified.');
            continue;
          }

          if (message.type === 'dechess-game-resume-final' && current) {
            const checkpoint = message.payload.checkpoint;
            if (
              !checkpointMatchesGame(checkpoint, current)
              || !isDualSignedCheckpoint(checkpoint, current)
            ) {
              throw new Error('Invalid finalized resume checkpoint');
            }
            setResumeCheckpoint(checkpoint);
            setNotice('Dual-signed resume checkpoint verified.');
            continue;
          }

          if (message.type === 'dechess-game-vdf-proof' && current?.rated) {
            const proof = message.payload.proof;
            if (
              !proof?.playerId
              || proof.playerId === identity.playerId
              || ![current.players.white.playerId, current.players.black.playerId]
                .includes(proof.playerId)
            ) {
              throw new Error('Invalid time-weave proof: player-mismatch');
            }
            // Lobby proxy + share intervals can redeliver the same proof often.
            if (vdfProofs.current[proof.playerId]) {
              continue;
            }
            const maxCheckpointSteps = Math.max(
              0,
              ...(proof.checkpoints || []).map((checkpoint) => Number(checkpoint.steps) || 0)
            );
            // Reject pathological live-weave proofs that would freeze the tab.
            if ((proof.totalSteps || 0) > 4096 || maxCheckpointSteps > 4096) {
              throw new Error('Invalid time-weave proof: proof-too-expensive');
            }
            const verification = await verifyTimeWeaveProof(
              proof,
              current,
              proof.playerId
            );
            if (!verification.valid) {
              throw new Error(`Invalid time-weave proof: ${verification.errors.join(', ')}`);
            }
            vdfProofs.current[proof.playerId] = proof;
            setVdfProofVersion((version) => version + 1);
            setNotice('Opponent time-weave proof verified.');
            lobbyDebug('game-vdf-opponent-accepted', {
              gameId,
              playerId: proof.playerId,
              totalSteps: proof.totalSteps
            });
            continue;
          }

          if (message.type === 'dechess-game-certificate-proposal' && current) {
            const proposed = message.payload.certificate;
            const proposer = [current.players.white, current.players.black]
              .find((player) => player?.playerId === message.payload.signerId);
            if (!proposer || !verifyCertificateSignature(proposed, proposer)) {
              throw new Error('Invalid certificate proposal signature');
            }
            const expectedHeads = {
              [current.players.white.playerId]: current.players.white.chainHead || null,
              [current.players.black.playerId]: current.players.black.chainHead || null
            };
            if (
              proposed.chainPrev?.[current.players.white.playerId]
                !== expectedHeads[current.players.white.playerId]
              || proposed.chainPrev?.[current.players.black.playerId]
                !== expectedHeads[current.players.black.playerId]
            ) {
              throw new Error('Certificate chain head mismatch');
            }

            let signed = signResultCertificate(proposed, identity);
            const verificationOptions = {
              game: current,
              verifyVdfProof: async (proof, _certificate, player) =>
                (await verifyTimeWeaveProof(proof, current, player.playerId)).valid
            };
            const verification = await verifyResultCertificate(
              signed,
              verificationOptions
            );
            if (!verification.valid) {
              throw new Error(`Invalid certificate: ${verification.errors.join(', ')}`);
            }
            signed = await finalizeResultCertificate(signed, verificationOptions);
            let persisted = cloneCertificateForStore(signed);
            try {
              persisted = await saveCertificate(signed, verificationOptions);
            } catch (saveError) {
              lobbyDebug('certificate-countersign-save-fail', {
                gameId: current.id,
                message: saveError?.message || String(saveError)
              });
            }
            setResultCertificate(persisted);
            const finalPayload = { certificate: persisted };
            // Local wire first: board PeerJS is often down; lobby fanout is the
            // reliable path. Still share even when local save raced/failed so the
            // peer can echo a durable copy back.
            publishLocalGameWire({
              gameId: current.id,
              messageType: 'dechess-game-certificate-final',
              payload: finalPayload,
              senderId: identity.peerId,
              source: 'board'
            });
            if (runningNode) {
              void sendGameDirect(
                'dechess-game-certificate-final',
                finalPayload,
                [message.senderId].filter(Boolean)
              ).catch(() => undefined);
            }
            setNotice(
              persisted?.certificateHash
                ? 'Dual-signed result certificate verified and saved.'
                : 'Dual-signed result shared; waiting to confirm local save…'
            );
            continue;
          }

          if (message.type === 'dechess-game-certificate-final' && current) {
            const certificate = message.payload.certificate;
            const verificationOptions = {
              game: current,
              verifyVdfProof: async (proof, _resultCertificate, player) =>
                (await verifyTimeWeaveProof(proof, current, player.playerId)).valid
            };
            const verification = await verifyResultCertificate(
              certificate,
              verificationOptions
            );
            if (!verification.valid) {
              throw new Error(`Invalid finalized certificate: ${verification.errors.join(', ')}`);
            }
            const persisted = await saveCertificate(certificate, verificationOptions);
            setResultCertificate(persisted);
            setNotice('Dual-signed result certificate verified and saved.');
            // White (proposer) echoes once so the countersigner can persist the
            // same JSON wire copy if their local freeze/save raced and dropped.
            if (
              current.players.white.playerId === identity.playerId
              && !echoedCertificateHashes.current.has(persisted.certificateHash)
            ) {
              echoedCertificateHashes.current.add(persisted.certificateHash);
              const echoPayload = { certificate: persisted };
              publishLocalGameWire({
                gameId: current.id,
                messageType: 'dechess-game-certificate-final',
                payload: echoPayload,
                senderId: identity.peerId,
                source: 'board'
              });
              if (runningNode) {
                void sendGameDirect(
                  'dechess-game-certificate-final',
                  echoPayload,
                  [message.senderId].filter(Boolean)
                ).catch(() => undefined);
              }
            }
            continue;
          }

          if (message.type === 'dechess-game-action' && current) {
            const action = message.payload.action;
            if (!action || action.actionId === current.lastActionId) {
              continue;
            }
            // Merge through preferGameState so a concurrent ready-start cannot
            // replace our local confirmation with the peer's one-sided fork.
            const applied = applySignedGameAction(current, action);
            const next = action.type === 'ready-start'
              ? preferGameState(current, applied)
              : applied;
            adoptGame(next, { replace: action.type !== 'ready-start' });
            if (
              action.type === 'move'
              && action.playerId !== identity.playerId
            ) {
              const last = next.moves.at(-1);
              // Check cue in adoptGame already plays the check tone.
              if (!(next.status === 'playing' && moveGivesCheck(last))) {
                playBoardMoveSound(last, { own: false });
              }
            }
            if (action.type === 'ready-start' && next.status === 'playing') {
              setNotice('Both players are ready. The clock has started.');
            } else if (action.type === 'ready-start') {
              setNotice('Ready to begin recorded. Waiting for your opponent.');
            } else if (action.type === 'join') {
              setNotice('Opponent joined. Begin when you are both online and ready to play.');
            } else {
              setNotice('Verified signed game action.');
            }
            await broadcast(
              'dechess-game-state',
              { snapshot: signedSnapshotForWire(next) },
              [message.senderId]
            );
          }
        } catch (error) {
          // A snapshot often arrives before the matching action. The action is
          // then stale because the revision already moved. That is not a fault.
          if (/stale game revision/i.test(error?.message || '')) {
            lobbyDebug('game-action-stale', {
              gameId,
              message: error.message
            });
            continue;
          }
          setNotice(`Rejected network update: ${error.message}`);
        }
      }
    };

    process();
  }, [
    adoptGame,
    bootstrapPeer,
    broadcast,
    gameId,
    identity,
    messages,
    requestedRole,
    runningNode,
    sendGameDirect,
    signedSnapshotForWire,
    wireTick
  ]);

  const shareLocalVdfProof = useCallback(async (proof, peers = gameConnectPeers) => {
    if (!proof || !gameId) {
      return;
    }
    const payload = { proof };
    // Primary path for lobby-challenge games: board PeerJS is often down while
    // the lobby tab still has the joiner link. Local wire → lobby fanout.
    publishLocalGameWire({
      gameId,
      messageType: 'dechess-game-vdf-proof',
      payload,
      senderId: identity.peerId,
      source: 'board'
    });
    lobbyDebug('game-vdf-wire-publish', {
      gameId,
      playerId: proof.playerId,
      totalSteps: proof.totalSteps
    });
    if (!runningNode) {
      return;
    }
    const targets = peers?.length
      ? peers
      : (connectionStats?.peerIds || []);
    // Never block finalization on PeerJS delivery.
    void sendGameDirect('dechess-game-vdf-proof', payload, targets).catch(() => undefined);
  }, [
    connectionStats?.peerIds,
    gameConnectPeers,
    gameId,
    identity.peerId,
    runningNode,
    sendGameDirect
  ]);
  const shareLocalVdfProofRef = useRef(shareLocalVdfProof);
  shareLocalVdfProofRef.current = shareLocalVdfProof;

  // Rated end-of-game proof: must NOT require discovery-room join or a live
  // PeerJS node. Lobby-challenge host boards often play only via the lobby proxy.
  useEffect(() => {
    if (
      !game?.rated
      || game.status !== 'finished'
      || !colorForPlayer(game, identity.playerId)
    ) {
      return undefined;
    }
    if (vdfProofs.current[identity.playerId]) {
      return undefined;
    }

    let cancelled = false;
    const attemptId = `${game.id}:${game.revision}:${Date.now()}`;

    const ensureLocalVdfProof = async ({ force = false } = {}) => {
      if (cancelled || vdfProofs.current[identity.playerId]) {
        return;
      }
      if (timeWeaveFinalizing.current && !force) {
        return;
      }
      timeWeaveFinalizing.current = true;
      const live = timeWeave.current;
      timeWeave.current = null;
      if (live) {
        try {
          live.cancel();
        } catch {
          // Ignore cancel races.
        }
      }
      lobbyDebug('game-vdf-ensure-start', {
        gameId: game.id,
        attemptId,
        force,
        hasNode: Boolean(runningNode)
      });
      try {
        // Fixed lightweight calibration — do not block on slow/localStorage calibrate.
        const calibration = {
          measuredAt: Date.now(),
          stepsPerSecond: 1000
        };
        const proof = await computeTimeWeaveProof({
          game,
          playerId: identity.playerId,
          calibration,
          stepsPerCheckpoint: 1
        });
        const liveGame = gameRef.current;
        const sameFinish = Boolean(
          liveGame
          && liveGame.id === game.id
          && liveGame.status === 'finished'
          && liveGame.revision === game.revision
          && liveGame.startedAt === game.startedAt
          && (liveGame.moves?.length || 0) === (game.moves?.length || 0)
        );
        // A clock tick or node identity change must not throw away a proof for
        // the same finished contract. A new start time or transcript must.
        if (!sameFinish || vdfProofs.current[identity.playerId]) {
          return;
        }
        vdfProofs.current[identity.playerId] = proof;
        setVdfProofVersion((version) => version + 1);
        setNotice('Local time-weave proof completed and shared.');
        lobbyDebug('game-vdf-ensure-done', {
          gameId: game.id,
          attemptId,
          totalSteps: proof.totalSteps
        });
        void shareLocalVdfProofRef.current(proof);
      } catch (error) {
        lobbyDebug('game-vdf-ensure-fail', {
          gameId: game.id,
          attemptId,
          message: error?.message || String(error)
        });
        if (!cancelled) {
          setNotice(`Time-weave failed: ${error.message}`);
        }
      } finally {
        timeWeaveFinalizing.current = false;
      }
    };

    void ensureLocalVdfProof();
    // Watchdog: unstick a wedged finalizing flag / missed attempt.
    const timer = window.setInterval(() => {
      if (cancelled || vdfProofs.current[identity.playerId]) {
        return;
      }
      void ensureLocalVdfProof({ force: true });
    }, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [
    game,
    identity.playerId,
    vdfProofVersion
  ]);

  // A concurrent ready-start used to leave each board with a different
  // startedAt. Heal to the shared contract, then recompute. An already stored
  // proof would otherwise be rejected by the opponent forever.
  useEffect(() => {
    if (!game?.rated || game.status !== 'finished' || !game.startedAt) {
      return undefined;
    }
    const localProof = vdfProofs.current[identity.playerId];
    if (!localProof) {
      return undefined;
    }
    let cancelled = false;
    gameContractHash(game).then((seed) => {
      if (cancelled || !vdfProofs.current[identity.playerId]) {
        return;
      }
      if (vdfProofs.current[identity.playerId].contractSeed === seed) {
        return;
      }
      delete vdfProofs.current[identity.playerId];
      setVdfProofVersion((version) => version + 1);
      lobbyDebug('game-vdf-contract-changed', { gameId: game.id, seed });
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [game, identity.playerId]);

  // Optional background weave while playing (anti-farm work). Never used to
  // block game-over finalization.
  useEffect(() => {
    if (
      !game?.rated
      || game.status !== 'playing'
      || !colorForPlayer(game, identity.playerId)
    ) {
      return;
    }
    if (timeWeave.current) {
      timeWeave.current.observeGame(game);
      return;
    }
    if (timeWeaveStarting.current || vdfProofs.current[identity.playerId]) {
      return;
    }
    timeWeaveStarting.current = true;
    loadOrCalibrateVdf()
      .then((calibration) =>
        createLiveTimeWeave({
          game,
          playerId: identity.playerId,
          calibration
        })
      )
      .then((controller) => {
        timeWeaveStarting.current = false;
        const current = gameRef.current;
        if (!current || current.status !== 'playing') {
          try {
            controller.cancel();
          } catch {
            // Ignore.
          }
          return;
        }
        timeWeave.current = controller;
        controller.observeGame(current);
        setNotice('Rated time-weave is running in a background worker.');
      })
      .catch((error) => {
        timeWeaveStarting.current = false;
        setNotice(`Could not start time-weave: ${error.message}`);
      });
  }, [game, identity.playerId]);

  useEffect(() => {
    if (
      !game?.rated
      || game.status !== 'finished'
      || !vdfProofs.current[identity.playerId]
      || !seatedOpponentPlayerId
      || vdfProofs.current[seatedOpponentPlayerId]
    ) {
      return undefined;
    }

    const shareProof = () => {
      shareLocalVdfProof(vdfProofs.current[identity.playerId]).catch(() => undefined);
    };

    shareProof();
    const timer = setInterval(shareProof, 4000);
    return () => clearInterval(timer);
  }, [
    game,
    identity.playerId,
    seatedOpponentPlayerId,
    shareLocalVdfProof,
    vdfProofVersion
  ]);

  useEffect(
    () => () => {
      timeWeave.current?.cancel();
    },
    []
  );

  useEffect(() => {
    if (
      game?.status !== 'finished'
      || game.result?.endReason === 'abandoned'
      || game.players.white.playerId !== identity.playerId
      || certificateSent.current
      || (
        game.rated
        && !hasBothVdfProofs(game, vdfProofs.current)
      )
    ) {
      return;
    }
    certificateSent.current = true;

    const proposeCertificate = async () => {
      try {
        const draft = await createResultCertificateDraft({
          game,
          chainHeads: {
            [game.players.white.playerId]: game.players.white.chainHead || null,
            [game.players.black.playerId]: game.players.black.chainHead || null
          },
          vdfProofs: vdfProofs.current
        });
        const signed = signResultCertificate(draft, identity);
        const payload = {
          certificate: signed,
          signerId: identity.playerId
        };
        publishLocalGameWire({
          gameId: game.id,
          messageType: 'dechess-game-certificate-proposal',
          payload,
          senderId: identity.peerId,
          source: 'board'
        });
        const targets = gameConnectPeers.length
          ? gameConnectPeers
          : (connectionStats?.peerIds || []);
        if (runningNode) {
          void sendGameDirect(
            'dechess-game-certificate-proposal',
            payload,
            targets
          ).catch(() => undefined);
        }
        setNotice(
          game.rated
            ? 'Result certificate sent for opponent signature.'
            : 'Unrated result certificate offered to opponent.'
        );
      } catch (error) {
        certificateSent.current = false;
        setNotice(`Certificate proposal failed: ${error.message}`);
      }
    };

    void proposeCertificate();
  }, [
    connectionStats?.peerIds,
    gameConnectPeers,
    game,
    identity,
    runningNode,
    sendGameDirect,
    vdfProofVersion
  ]);

  const sendAction = useCallback(
    async (type, payload = {}) => {
      const current = gameRef.current;
      if (!current) {
        return;
      }
      try {
        const action = createSignedGameAction({
          game: current,
          type,
          payload,
          identity
        });
        const next = applySignedGameAction(current, action);
        if (type === 'move') {
          unlockMoveSounds();
        }
        adoptGame(next, { replace: true });
        // Lobby tab relays to the joiner when this board has no direct PeerJS link.
        publishLocalGameUpdate(next, { source: 'host-board-action' });
        const snapshot = signedSnapshotForWire(next);
        // Fan the signed ply immediately — action + snapshot — to seats and
        // spectators. Do not await: local board already advanced.
        publishLocalGameWire({
          gameId: current.id,
          messageType: 'dechess-game-action',
          payload: { action },
          senderId: identity.peerId,
          source: 'board'
        });
        publishLocalGameWire({
          gameId: current.id,
          messageType: 'dechess-game-state',
          payload: { snapshot },
          senderId: identity.peerId,
          source: 'board'
        });
        if (type === 'move') {
          const last = next.moves.at(-1);
          // Check cue in adoptGame already plays the check tone.
          if (!(next.status === 'playing' && moveGivesCheck(last))) {
            playBoardMoveSound(last, { own: true });
          }
        }
        const targets = gameFanoutPeers.length
          ? gameFanoutPeers
          : (connectionStats?.peerIds || []);
        const deliver = async () => {
          await sendGameDirect(
            'dechess-game-action',
            { action },
            targets
          );
          await sendGameDirect(
            'dechess-game-state',
            { snapshot },
            targets
          ).catch(() => undefined);
        };
        if (type === 'move' || type === 'resign' || type === 'claim-flag' || type === 'offer-draw' || type === 'accept-draw' || type === 'decline-draw') {
          void deliver().catch((error) => {
            lobbyDebug('game-move-deliver-fail', {
              gameId: current.id,
              type,
              message: error?.message || String(error)
            });
          });
        } else {
          await deliver();
        }
        if (next.status === 'finished' || next.status === 'aborted') {
          publishLocalGameUpdate(next, { source: 'host-board-terminal' });
        }
        if (type === 'ready-start' && next.status === 'playing') {
          setNotice('Both players are ready. The clock has started.');
        } else if (type === 'ready-start') {
          setNotice('You confirmed. Waiting for your opponent.');
        } else if (type !== 'move') {
          setNotice(`Signed ${type} sent.`);
        }
      } catch (error) {
        setNotice(error.message);
      }
    },
    [
      adoptGame,
      connectionStats?.peerIds,
      gameFanoutPeers,
      identity,
      sendGameDirect,
      signedSnapshotForWire
    ]
  );

  const myColor = game ? colorForPlayer(game, identity.playerId) : null;
  const clocks = game ? liveClocks(game, now) : { white: 0, black: 0 };
  // Challenge joins often seat black via the lobby peer while the host board tab
  // still has zero direct links — still allow ready-start; lobby relays it.
  const opponentConnected = Boolean(
    game?.players?.black
    && (
      connectionStats.openCount > 0
      || gameConnectPeers.length > 0
      || discoveredPeers.length > 0
      || Boolean(urlBootstrapPeer)
      || game.status === 'starting'
      || game.status === 'playing'
    )
  );
  const canMove = Boolean(
    game?.status === 'playing' && myColor && game.turn === myColor
  );
  const opponentColor = myColor === 'black' ? 'white' : 'black';
  const myReady = Boolean(game?.startReady?.[myColor]);
  const opponentReady = Boolean(game?.startReady?.[opponentColor]);
  const canConfirmStart = Boolean(
    game?.status === 'starting'
    && myColor
    && opponentConnected
    && !myReady
  );
  const joinDeadlineRemaining = game?.joinDeadlineAt
    ? Math.max(0, game.joinDeadlineAt - now)
    : null;
  const scheduledStartRemaining = game?.scheduledStartAt && game.scheduledStartAt > now
    ? game.scheduledStartAt - now
    : null;
  const forfeitWindowRemaining = game?.scheduledStartAt
    ? Math.max(0, (forfeitDeadlineAt(game) ?? now) - now)
    : null;
  const canClaimForfeit = Boolean(
    game && myColor && canClaimStartForfeit(game, identity.playerId, now)
  );
  const inviteUrl = useMemo(() => {
    const url = new URL(window.location.origin);
    url.searchParams.set('game', gameId);
    url.searchParams.set('role', 'join');
    url.searchParams.set('peer', identity.peerId);
    return url.toString();
  }, [gameId, identity.peerId]);
  const spectatorUrl = useMemo(() => {
    const url = new URL(inviteUrl);
    url.searchParams.set('role', 'spectator');
    return url.toString();
  }, [inviteUrl]);
  const spectatorCount = peers.filter(
    (peer) => peer.metadata?.role === 'spectator'
  ).length;

  const resumeUrl = useMemo(() => {
    if (!resumeCheckpoint || !game || !isDualSignedCheckpoint(resumeCheckpoint, game)) {
      return null;
    }
    const url = new URL(inviteUrl);
    url.searchParams.set('resume', encodeResumeCheckpoint(resumeCheckpoint));
    return url.toString();
  }, [game, inviteUrl, resumeCheckpoint]);

  async function copyLink(value = inviteUrl) {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  function confirmRecoveryBackup() {
    markRecoveryBackupAcknowledged(identity.playerId);
    setRecoveryGateOpen(false);
  }

  async function requestResumeCheckpoint() {
    if (!game || !myColor || !game.players.black) {
      return;
    }
    const checkpoint = signResumeCheckpoint(createResumeCheckpoint(game), identity);
    setResumeCheckpoint(checkpoint);
    const payload = { checkpoint, signerId: identity.playerId };
    // Lobby-challenge boards often have no direct PeerJS link; relay via lobby.
    publishLocalGameWire({
      gameId: game.id,
      messageType: 'dechess-game-resume-proposal',
      payload,
      senderId: identity.peerId,
      source: 'board'
    });
    const targets = gameConnectPeers.length
      ? gameConnectPeers
      : (connectionStats?.peerIds || []);
    if (runningNode) {
      await sendGameDirect('dechess-game-resume-proposal', payload, targets);
    }
    setNotice('Resume checkpoint sent for opponent signature.');
  }

  function exportMatchFile() {
    if (!game) {
      return;
    }
    try {
      const filename = downloadMatchExport(buildMatchExport(game, identity));
      setNotice(`Match exported as ${filename}. Import it later from the lobby to continue.`);
    } catch (error) {
      setNotice(error.message || 'Could not export this match');
    }
  }

  if (!game) {
    return (
      <>
        <main className="game-shell waiting-screen">
          <a href={publicUrl()} className="back-link">← Lobby</a>
          <div className="panel">
            <p className="eyebrow">Game {gameId.slice(0, 8)}</p>
            <h1>Connecting to game…</h1>
            <p className="muted">{nodeError?.message || roomError?.message || notice}</p>
            <p>{connectionStats.openCount} peer links</p>
            {discoveredPeers.length > 0 && (
              <p className="muted">Game peers discovered: {discoveredPeers.length}</p>
            )}
            <p className="muted">{joined ? 'Room joined — syncing board…' : 'Joining game room…'}</p>
          </div>
        </main>
        <RecoveryPhraseModal
          open={recoveryGateOpen}
          onConfirm={confirmRecoveryBackup}
        />
      </>
    );
  }

  const topColor = myColor ? opponentColor : 'black';
  const bottomColor = topColor === 'white' ? 'black' : 'white';
  const topPlayer = game.players[topColor];
  const bottomPlayer = game.players[bottomColor];
  const localVdfReady = Boolean(vdfProofs.current[identity.playerId]);
  const opponentVdfReady = Boolean(
    seatedOpponentPlayerId && vdfProofs.current[seatedOpponentPlayerId]
  );
  const ratedWeaveStatus = !localVdfReady
    ? 'Time-weave running'
    : opponentVdfReady
      ? 'Time-weave complete'
      : 'Waiting for opponent time-weave';
  const certificateWaitMessage = !game.rated
    ? 'Unrated result recorded locally.'
    : !localVdfReady
      ? 'Finalizing local time-weave proof…'
      : !opponentVdfReady
        ? 'Waiting for opponent time-weave proof…'
        : 'Waiting for certificate signatures…';

  return (
    <>
      <main className="game-shell">
      <header className="game-header">
        <a href={publicUrl()} className="back-link">← Lobby</a>
        <div>
          <p className="eyebrow">
            {(game.variant && game.variant !== 'standard'
              ? `${VARIANTS[game.variant]?.label || game.variant} · `
              : '')}
            {game.timeControl} · {requestedRole === 'spectator'
              ? 'Spectating'
              : game.rated
                ? 'Proof-backed rated'
                : 'Unrated'}
          </p>
          <strong>{
            game.status === 'waiting'
              ? 'Waiting for opponent'
              : game.status === 'starting'
                ? 'Ready to begin'
                : `Game ${game.id.slice(0, 8)}`
          }</strong>
        </div>
        <div className={`status ${joined ? 'online' : ''}`}>
          <span className="status-dot" />
          {joined
            ? game.rated
              ? ratedWeaveStatus
              : `${spectatorCount} spectator${spectatorCount === 1 ? '' : 's'}`
            : status}
        </div>
      </header>

      <div className="game-layout">
        <section className="board-column">
          <div className={`player-strip ${game.status === 'playing' && game.turn === topColor ? 'active' : ''}`}>
            <span className="avatar">♟</span>
            <strong>{topPlayer ? formatDisplayName(topPlayer.nickname, topPlayer.playerId) : 'Waiting…'}</strong>
            <time>{formatClock(clocks[topColor])}</time>
          </div>

          <ChessBoard
            game={game}
            orientation={myColor || 'white'}
            disabled={!canMove}
            onMove={(move) => sendAction('move', move)}
            checkLabel={checkAlert ? t('game.check') : ''}
          />

          <div className={`player-strip ${game.status === 'playing' && game.turn === bottomColor ? 'active' : ''}`}>
            <span className="avatar">♙</span>
            <strong>{bottomPlayer ? formatDisplayName(bottomPlayer.nickname, bottomPlayer.playerId) : 'Waiting…'}</strong>
            <time>{formatClock(clocks[bottomColor])}</time>
          </div>
        </section>

        <aside className="game-sidebar">
          {(game.status === 'starting'
            || (game.status === 'waiting' && game.challengedPlayerId))
            && myColor && (
            <section className="panel start-game-panel">
              <p className="eyebrow">Match start procedure</p>
              <h2>
                {game.status === 'waiting' ? 'Challenge sent' : 'Confirm at the board'}
              </h2>
              {game.scheduledStartAt ? (
                <p className="muted">
                  Committed start: {formatChallengeDeadline(game.scheduledStartAt)}.
                  {' '}
                  {Math.round((game.forfeitGraceMs ?? MATCH_FORFEIT_GRACE_MS) / 60000)}-minute
                  forfeit grace applies if a player fails to confirm after the start time.
                </p>
              ) : game.status === 'waiting' ? (
                <p className="muted">
                  Waiting for {formatDisplayName(
                    getPlayerProfile(game.challengedPlayerId)?.nickname,
                    game.challengedPlayerId
                  )} to accept. Use Begin to Play once they are seated.
                </p>
              ) : (
                <p className="muted">
                  Both players must confirm online before the clock starts.
                </p>
              )}
              {!game.players.black ? (
                <p className="muted">
                  {game.joinDeadlineAt
                    ? (joinDeadlineRemaining > 0
                      ? `Waiting for opponent to join (${formatCountdown(joinDeadlineRemaining)} left).`
                      : 'Join window expired.')
                    : 'Waiting for your opponent to take a seat.'}
                </p>
              ) : myReady && !opponentReady ? (
                <p className="muted">
                  You are confirmed. Waiting for {formatDisplayName(
                    game.players[opponentColor]?.nickname,
                    game.players[opponentColor]?.playerId
                  )} to confirm.
                </p>
              ) : !opponentConnected ? (
                <p className="muted">
                  Waiting for a path to your opponent (lobby relay or direct peer link).
                </p>
              ) : scheduledStartRemaining > 0 ? (
                <p className="muted">
                  Start in {formatCountdown(scheduledStartRemaining)}.
                  You may confirm early; the clock starts at the committed time when both are present.
                </p>
              ) : forfeitWindowRemaining > 0 && !opponentReady ? (
                <p className="muted">
                  Forfeit window: {formatCountdown(forfeitWindowRemaining)} remaining for your opponent to confirm.
                </p>
              ) : connectionStats.openCount === 0 ? (
                <p className="muted">
                  Opponent seated. Confirm to begin — the lobby tab will relay if needed.
                </p>
              ) : null}
              <button
                type="button"
                className="create-game-button"
                disabled={!canConfirmStart}
                onClick={() => sendAction('ready-start')}
              >
                {game.scheduledStartAt ? 'Confirm at board' : 'Begin to Play'}
              </button>
              {canClaimForfeit && (
                <button
                  type="button"
                  className="danger"
                  onClick={() => sendAction('claim-start-forfeit')}
                >
                  Claim win by forfeit
                </button>
              )}
              <button
                type="button"
                className="secondary"
                onClick={() => sendAction('abort')}
              >
                Cancel game
              </button>
            </section>
          )}

          {game.status === 'waiting' && !game.challengedPlayerId && (
            <section className="panel challenge-card">
              <p className="eyebrow">Direct challenge</p>
              <h2>Invite an opponent</h2>
              <p className="muted">This link bootstraps the other browser directly into this game scope.</p>
              {game.joinDeadlineAt && (
                <p className="muted">
                  {joinDeadlineRemaining > 0
                    ? `Waiting for opponent to join (${formatCountdown(joinDeadlineRemaining)} left).`
                    : 'Join window expired.'}
                  {' '}
                  Deadline: {formatChallengeDeadline(game.joinDeadlineAt)}.
                </p>
              )}
              {game.scheduledStartAt && (
                <p className="muted">
                  Committed start: {formatChallengeDeadline(game.scheduledStartAt)}.
                  {scheduledStartRemaining > 0
                    ? ` Begins in ${formatCountdown(scheduledStartRemaining)}.`
                    : ' Confirm at the board before the forfeit window closes.'}
                  {' '}
                  ({Math.round((game.forfeitGraceMs ?? MATCH_FORFEIT_GRACE_MS) / 60000)} min grace)
                </p>
              )}
              <label htmlFor="challenge-link">Challenge link</label>
              <input id="challenge-link" value={inviteUrl} readOnly />
              <button type="button" onClick={() => copyLink(inviteUrl)}>
                {copied ? 'Copied' : 'Copy challenge link'}
              </button>
            </section>
          )}

          {game.result && (
            <section className="panel result-card">
              <p className="eyebrow">Game over</p>
              <h2>{game.result.value || 'Aborted'}</h2>
              <p>{game.result.endReason}</p>
              {resultCertificate ? (
                <div className="certificate-proof">
                  <span>✓ Dual-signed certificate</span>
                  <code title={resultCertificate.certificateHash}>
                    {resultCertificate.certificateHash.slice(0, 16)}…
                  </code>
                  <details>
                    <summary>Verify proof</summary>
                    <p>
                      2/2 Ed25519 signatures ·{' '}
                      {resultCertificate.rated
                        ? `${Object.values(resultCertificate.vdfProofs)
                          .reduce((sum, proof) => sum + proof.checkpoints.length, 0)} VDF checkpoints`
                        : 'unrated, no VDF required'}
                    </p>
                  </details>
                </div>
              ) : game.result.endReason === 'abandoned' ? null : game.rated ? (
                <small className="muted">{certificateWaitMessage}</small>
              ) : (
                <small className="muted">Unrated result recorded locally.</small>
              )}
            </section>
          )}

          {myColor && game.players.black && (
            <section className="panel spectator-panel">
              <p className="eyebrow">Live audience</p>
              <h2>{spectatorCount} spectator{spectatorCount === 1 ? '' : 's'}</h2>
              <p className="muted">
                Spectators receive verified signed snapshots and cannot submit moves.
              </p>
              <label htmlFor="spectator-link">Spectator link</label>
              <input id="spectator-link" value={spectatorUrl} readOnly />
              <button
                type="button"
                className="secondary"
                onClick={() => copyLink(spectatorUrl)}
              >
                {copied ? 'Copied' : 'Copy spectator link'}
              </button>
            </section>
          )}

          <section className="panel moves-panel">
            <div className="section-heading">
              <h2>Moves</h2>
              <span className="counter">rev {game.revision}</span>
            </div>
            <ol className="move-list">
              {game.moves.length === 0 && <li className="empty">No moves yet.</li>}
              {game.moves.map((move) => (
                <li key={move.actionId || move.ply}>
                  <span>{move.ply}.</span>
                  <strong>{move.san}</strong>
                  <small>{formatClock(move.clockAfterMs)}</small>
                </li>
              ))}
            </ol>
          </section>

          {game.status === 'playing' && myColor && (
            <section className="panel game-controls">
              {game.drawOffer && game.drawOffer !== myColor ? (
                <>
                  <p>Your opponent offers a draw.</p>
                  <button type="button" onClick={() => sendAction('accept-draw')}>Accept draw</button>
                  <button type="button" className="secondary" onClick={() => sendAction('decline-draw')}>Decline</button>
                </>
              ) : (
                <button type="button" className="secondary" onClick={() => sendAction('offer-draw')}>
                  Offer draw
                </button>
              )}
              {game.moves.length < 2 && (
                <button type="button" className="secondary" onClick={() => sendAction('abort')}>
                  Abort
                </button>
              )}
              <button type="button" className="danger" onClick={() => sendAction('resign')}>
                Resign
              </button>
              {game.turn !== myColor
                && Number.isFinite(clocks[game.turn])
                && clocks[game.turn] <= 0 && (
                <button type="button" onClick={() => sendAction('claim-flag')}>Claim flag</button>
              )}
            </section>
          )}

          <section className="panel resume-panel">
            <p className="eyebrow">Persistence</p>
            <h2>{game.rated ? 'Reconnect' : 'Export & continue later'}</h2>
            <p className="muted">
              {game.rated
                ? 'Rated games stay on the clock. Use a dual-signed resume link only to reconnect after a drop — casual export and pause are disabled for ladder integrity.'
                : isUntimedControl(game.timeControl)
                  ? 'Untimed casual games are made for pauses. Export a signed match file now, then import it from the lobby whenever both of you return.'
                  : 'Casual games can pause. Export a signed match file to continue later, or request a dual-signed resume link while both players are connected. Rated games cannot be exported.'}
            </p>
            <div className="resume-actions">
              {allowsMatchExport(game) && (
                <button
                  type="button"
                  className="secondary"
                  onClick={exportMatchFile}
                >
                  Export match file
                </button>
              )}
              {resumeUrl ? (
                <>
                  <span className="verified-badge">✓ Dual-signed resume link</span>
                  <button type="button" className="secondary" onClick={() => copyLink(resumeUrl)}>
                    {copied ? 'Copied' : 'Copy verified resume link'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="secondary"
                  disabled={!myColor || !game.players.black}
                  onClick={requestResumeCheckpoint}
                >
                  Request dual-signed resume link
                </button>
              )}
            </div>
          </section>
        </aside>
      </div>

      <footer>
        <span>{scope}</span>
        <span>{connectionStats.openCount} direct links</span>
        <span>{notice}</span>
        <ReleaseNote />
      </footer>
      </main>
      <RecoveryPhraseModal
        open={recoveryGateOpen}
        onConfirm={confirmRecoveryBackup}
      />
    </>
  );
}
